import type { TFunction } from 'i18next';
import type { MeasureMetadata } from '../../../api/work-periods-client/work-periods-client.type';
import type { FloraContextualInfo } from '../../flora/flora.type';
import type { ChartDataItem, LineItem } from '../components/line-chart/line-chart.type';
import type { CapacityTableRow } from '../main-section/capacity/table/table.type';
import type { Row as InitiativeOverviewRow } from '../main-section/initiative-overview/table/table.type';
import type { MonthlyAveragesTableRow } from '../main-section/monthly-averages/table/table.type';
import { formatMeasureName } from '../process-analysis.helpers';

/**
 * Formats measure comparison chart data for Flora consumption.
 * Only includes measures that are currently selected/visible in the chart.
 *
 * @param {ChartDataItem[]} chartData - The chart data array
 * @param {LineItem[]} selectedMeasures - Array of selected measures to include
 * @param {MeasureMetadata[]} measureMetadata - Metadata with measure titles
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, unknown>} Formatted data with context
 */
const formatMeasureComparisonForFlora = (
  chartData: ChartDataItem[],
  selectedMeasures: LineItem[],
  measureMetadata: MeasureMetadata[],
  contextualInfo: FloraContextualInfo | null,
  t: TFunction,
): Record<string, unknown> => {
  const measureNameToTitle: Record<string, string> = {};
  const measureDescriptions: Record<string, string> = {};
  measureMetadata.forEach((m) => {
    measureNameToTitle[m.measure_name] = m.measure_title;
    if (m.measure_description) {
      measureDescriptions[m.measure_title] = m.measure_description;
    }
  });

  const selectedMeasureIds = new Set(selectedMeasures.map((m) => m.id));

  const formatted: Record<string, Record<string, number | null>> = {};

  chartData.forEach((dataPoint) => {
    const dateKey = dataPoint.date;
    const measureValues: Record<string, number | null> = {};

    selectedMeasureIds.forEach((measureId) => {
      const value = dataPoint[measureId];
      if (value !== undefined) {
        const title = measureNameToTitle[measureId] || formatMeasureName(measureId, t);
        measureValues[title] = typeof value === 'number' ? value : null;
      }
    });

    if (Object.keys(measureValues).length > 0) {
      formatted[dateKey] = measureValues;
    }
  });

  return {
    ...contextualInfo,
    measureDescriptions,
    data: formatted,
  };
};

/**
 * Formats key measures over time chart data for Flora consumption.
 * Includes the selected measure across all selected entities.
 *
 * @param {ChartDataItem[]} chartData - The chart data array
 * @param {string} selectedMeasure - The selected measure name
 * @param {Record<string, string>} entityNames - Mapping of entity IDs to names
 * @param {LineItem[]} selectedEntities - Array of selected entities
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, unknown>} Formatted data with context
 */
const formatKeyMeasuresOverTimeForFlora = (
  chartData: ChartDataItem[],
  selectedMeasure: string,
  entityNames: Record<string, string>,
  selectedEntities: LineItem[],
  contextualInfo: FloraContextualInfo | null,
  t: TFunction,
): Record<string, unknown> => {
  const selectedEntityIds = new Set(selectedEntities.map((e) => e.id));

  const formatted: Record<string, Record<string, number | null>> = {};

  chartData.forEach((dataPoint) => {
    const dateKey = dataPoint.date;
    const entityValues: Record<string, number | null> = {};

    selectedEntityIds.forEach((entityId) => {
      const value = dataPoint[entityId];
      if (value !== undefined) {
        const entityName = entityNames[entityId] || entityId;
        entityValues[entityName] = typeof value === 'number' ? value : null;
      }
    });

    if (Object.keys(entityValues).length > 0) {
      formatted[dateKey] = entityValues;
    }
  });

  return {
    ...contextualInfo,
    measure: formatMeasureName(selectedMeasure, t),
    data: formatted,
  };
};

/**
 * Formats initiative chart data (completion or focus) for Flora consumption.
 * Only includes initiatives that are currently selected/visible.
 *
 * @param {ChartDataItem[]} chartData - The chart data array
 * @param {LineItem[]} selectedInitiatives - Array of selected initiatives to include
 * @param {'completion' | 'focus'} chartType - Type of initiative chart
 * @param {string} unitType - The unit type label (e.g., 'Tasks' or 'Points')
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @returns {Record<string, unknown>} Formatted data with context
 */
const formatInitiativeChartForFlora = (
  chartData: ChartDataItem[],
  selectedInitiatives: LineItem[],
  chartType: 'completion' | 'focus',
  unitType: string,
  contextualInfo: FloraContextualInfo | null,
): Record<string, unknown> => {
  const selectedInitiativeIds = new Set(selectedInitiatives.map((i) => i.id));

  const formatted: Record<string, Record<string, number | null>> = {};

  chartData.forEach((dataPoint) => {
    const dateKey = dataPoint.date;
    const initiativeValues: Record<string, number | null> = {};

    selectedInitiativeIds.forEach((initiativeId) => {
      const value = dataPoint[initiativeId];
      if (value !== undefined) {
        const initiative = selectedInitiatives.find((i) => i.id === initiativeId);
        const name = initiative?.name || initiativeId;
        initiativeValues[name] = typeof value === 'number' ? value : null;
      }
    });

    if (Object.keys(initiativeValues).length > 0) {
      formatted[dateKey] = initiativeValues;
    }
  });

  return {
    ...contextualInfo,
    chartType,
    unitType,
    data: formatted,
  };
};

/**
 * Formats capacity table data for Flora consumption.
 * Only includes rows currently displayed in the table.
 *
 * @param {CapacityTableRow[]} tableData - The capacity table rows
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @returns {Record<string, unknown>} Formatted capacity data with context
 */
const formatCapacityForFlora = (
  tableData: CapacityTableRow[],
  contextualInfo: FloraContextualInfo | null,
): Record<string, unknown> => {
  const data = tableData.map((row) => ({
    team: row.name,
    tasks: row.tasks,
    points: row.points,
    distribution: `${(row.distribution ?? 0).toFixed(0)}%`,
  }));

  return {
    ...contextualInfo,
    data,
  };
};

/**
 * Formats initiative overview table data for Flora consumption.
 * Uses the already filtered table data (Row[]).
 *
 * @param {InitiativeOverviewRow[]} tableData - Filtered initiative table rows
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @returns {Record<string, unknown>} Formatted initiative data with context
 */
const formatInitiativeOverviewForFlora = (
  tableData: InitiativeOverviewRow[],
  contextualInfo: FloraContextualInfo | null,
): Record<string, unknown> => {
  const data = tableData.map((row) => ({
    name: row.name,
    status: row.status,
    startDate: row.startDate || '',
    endDate: row.endDate || '',
    completion: `${row.completion}%`,
  }));

  return {
    ...contextualInfo,
    data,
  };
};

/**
 * Formats monthly averages table data for Flora consumption.
 * Uses the already filtered table data.
 *
 * @param {Record<string, MonthlyAveragesTableRow[]>} filteredData - Filtered monthly averages data grouped by category
 * @param {string[]} selectedEntities - Selected entity IDs
 * @param {Record<string, string>} entityNames - Mapping of entity IDs to names
 * @param {FloraContextualInfo} contextualInfo - Date range and entity context
 * @returns {Record<string, unknown>} Formatted data with context
 */
const formatMonthlyAveragesForFlora = (
  filteredData: Record<string, MonthlyAveragesTableRow[]>,
  selectedEntities: string[],
  entityNames: Record<string, string>,
  contextualInfo: FloraContextualInfo | null,
): Record<string, unknown> => {
  const formatted: Record<string, Record<string, Record<string, number | null>>> = {};

  Object.entries(filteredData).forEach(([groupName, rows]) => {
    const groupData: Record<string, Record<string, number | null>> = {};

    rows.forEach((row) => {
      const entityValues: Record<string, number | null> = {};

      selectedEntities.forEach((entityId) => {
        const value = row[entityId];
        if (value !== undefined && typeof value === 'number') {
          const entityName = entityNames[entityId] || entityId;
          entityValues[entityName] = value;
        }
      });

      if (Object.keys(entityValues).length > 0) {
        groupData[row.measureTitle] = entityValues;
      }
    });

    if (Object.keys(groupData).length > 0) {
      formatted[groupName] = groupData;
    }
  });

  return {
    ...contextualInfo,
    data: formatted,
  };
};

export {
  formatCapacityForFlora,
  formatInitiativeChartForFlora,
  formatInitiativeOverviewForFlora,
  formatKeyMeasuresOverTimeForFlora,
  formatMeasureComparisonForFlora,
  formatMonthlyAveragesForFlora,
};
